<script setup lang="ts">
import type { ComponentPublicInstance, FunctionalComponent, Ref, VNode } from "vue";
import { computed, h, nextTick, onMounted, ref, watch } from "vue";

import { cardActions } from "@/action/cardActions";
import { cardKey, cardMetaKey } from "@/components/StickyNote/injectKeys";
import AttributeChip from "@/components/chip/AttributeChip.vue";
import StickyReactions from "@/components/sticky/StickyReactions/StickyReactions.vue";
import IconEmoji from "@/icons/general/sticky-reactions.svg?component";
import IconHot from "@/icons/reaction/hot.svg?component";
import IconIncrement from "@/icons/reaction/increment.svg?component";
import IconLove from "@/icons/reaction/love.svg?component";
import type { Reaction } from "@/model/card";
import { getReactionLabel, reactions } from "@/model/card";
import { animateCopy } from "@/utils/dom/animation";
import { keysIn } from "@/utils/record";
import { injectStrict } from "@/utils/vue";

import UsersListTooltip from "./UsersListTooltip.vue";

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);

const stickyHasAllReactions = computed(() => {
  if (!card.reactions) return false;
  return Object.values(card.reactions).every((arr) => arr.length > 0);
});

// TODO this is very low level, could it be made simpler?
interface ReactionInfo {
  icon: FunctionalComponent;
  tooltip: Ref<"" | VNode>;
  ref: null | ComponentPublicInstance;
}

const data: Record<Reaction, ReactionInfo> = {
  hot: { icon: IconHot, tooltip: ref(""), ref: null },
  increment: { icon: IconIncrement, tooltip: ref(""), ref: null },
  love: { icon: IconLove, tooltip: ref(""), ref: null },
};

// wait setting the tooltip content to a VNode until the component is mounted
onMounted(() => {
  for (const reaction of reactions) {
    data[reaction].tooltip.value = h(UsersListTooltip, {
      card,
      reaction,
      icon: data[reaction].icon,
    });
  }
});

function setRef(reaction: Reaction) {
  return (cmp: ComponentPublicInstance) => {
    data[reaction].ref = cmp;
  };
}

async function handleToggleReaction(reaction: Reaction) {
  if (!cardMeta.isReadonly) {
    await cardActions.toggleReaction("action-menu", card.id, reaction);
  }
}

watch(
  () => card.reactions,
  (newReactions, oldReactions) => {
    if (!newReactions) return;
    keysIn(newReactions).forEach((reaction) => {
      const oldUsers = oldReactions?.[reaction] || [];
      const newUsers = newReactions[reaction];
      if (newUsers.length > oldUsers.length) {
        void animate(reaction);
      }
    });
  },
);

async function animate(reaction: Reaction) {
  await nextTick();
  const iconRef = data[reaction].ref?.$el;
  if (iconRef) {
    animateElement(iconRef);
  }
}

function animateElement(el: HTMLElement) {
  for (let i = 0; i < 5; i++) {
    const delay = Math.random() * 150;
    const onStarted = (el: HTMLElement) => {
      el.style.left = Math.random() * 500 - 250 + "px";
    };
    setTimeout(() => animateCopy(el, "floating", onStarted), delay);
  }
}

const usersNumberForReaction = (reaction: Reaction) => {
  return card.reactions?.[reaction].length;
};
</script>

<template>
  <AttributeChip
    v-if="!stickyHasAllReactions && !cardMeta.isReadonly"
    name="user-reactions-chip"
    :tooltip="$t('cardAction.reactions')"
    :dropdown-props="{ class: 'user-reactions', width: 'auto' }"
  >
    <template #icon>
      <IconEmoji />
    </template>
    <StickyReactions class="sticky-reactions" />
  </AttributeChip>
  <template v-for="(users, reaction) in card.reactions" :key="reaction">
    <AttributeChip
      v-if="users.length > 0"
      :id="'reaction-' + reaction + '-' + card.id"
      :tooltip="{ content: data[reaction].tooltip }"
      tooltip-with-context
      :text="users.length.toString()"
      :dropdown-props="{ class: 'user-reaction-chip' }"
      :trigger-aria-label="
        $t('label.reaction.usersNumberForReaction', {
          number: usersNumberForReaction(reaction),
          reaction: $t(getReactionLabel(reaction)),
        })
      "
      @click="handleToggleReaction(reaction)"
    >
      <template #icon>
        <component :is="data[reaction].icon" :ref="setRef(reaction)" class="reaction-icon" />
      </template>
    </AttributeChip>
  </template>
</template>

<style lang="scss">
.base-popup.content.user-reactions {
  border-radius: 60px;
  overflow: visible;
}
</style>

<style lang="scss" scoped>
@use "@/styles/board";

@keyframes float {
  100% {
    transform: translateX(board.len(10px));
  }
}

.reaction-icon {
  position: absolute;
  opacity: 1;
  height: 80%;
  top: 10%;
  left: 8%;
}

.floating {
  width: 20px;
  height: 20px;
  animation: float 0.6s -0.3s alternate infinite ease-in-out;
  transform: translateX(board.len(-100px));
  transition-property: top, left, opacity;
  transition-duration: 2s;
  transition-timing-function: linear;
  top: board.len(-1000px) !important;
  opacity: 0;
}

.sticky-reactions {
  height: 36px;
}

.dropdown-menu.user-reaction-chip {
  overflow: visible;
}
</style>
